import React from 'react'
import { styled } from 'baseui'
import SDButtonNew from '@/shared/components/SDComponentsNewDesign/SDButton/SDButton'

// ─── Flex utilities ──────────────────────────────────────────

export const FlexContainer = styled('div', {
  display: 'flex',
})

export const FlexCenter = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const FlexCenterY = styled('div', {
  display: 'flex',
  alignItems: 'center',
})

export const FlexBetween = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const FlexColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
})

export const FlexWrap = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
})

export const RelativeContainer = styled('div', {
  position: 'relative',
  backgroundColor: 'white',
  borderRadius: '.5rem',
})

/** @type {React.ComponentType<{ $marginTop?: string, $marginBottom?: string, children?: React.ReactNode }>} */
export const SpacingWrapper = styled('div', ({ $marginTop, $marginBottom }) => ({
  ...($marginTop && { marginTop: $marginTop }),
  ...($marginBottom && { marginBottom: $marginBottom }),
}))

// Reusable style objects for css({}) usage and composition
export const flexStyles = {
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerY: {
    display: 'flex',
    alignItems: 'center',
  },
  between: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
}

// ─── App Shell (grid layout) ─────────────────────────────────

const appShellBase = {
  display: 'grid',
  // `banner` row sits above sidebar+content. It is `auto`-sized so it
  // collapses to 0 when the CutOffersBanner self-hides.
  gridTemplateAreas: '"banner banner" "sidebar content" "footer footer"',
  gridTemplateColumns: '56px 1fr',
  gridTemplateRows: 'auto 1fr auto',
  minHeight: '100vh',
}

export const AppShell = styled('div', ({ $theme }) => ({
  ...appShellBase,
  background: `linear-gradient(116.82deg, ${$theme.colors.gradientBlueStart}, ${$theme.colors.gradientBlueEnd})`,
}))

export const AppShellWarning = styled('div', ({ $theme }) => ({
  ...appShellBase,
  background: `linear-gradient(-45deg, hsla(0,0%,100%,0), hsla(0,0%,100%,0) 10%, rgba(0,0,0,.7) 0, rgba(0,0,0,.7) 20%, hsla(0,0%,100%,0) 0, hsla(0,0%,100%,0) 30%, rgba(0,0,0,.7) 0, rgba(0,0,0,.7) 40%, hsla(0,0%,100%,0) 0, hsla(0,0%,100%,0) 50%, rgba(0,0,0,.7) 0, rgba(0,0,0,.7) 60%, hsla(0,0%,100%,0) 0, hsla(0,0%,100%,0) 70%, rgba(0,0,0,.7) 0, rgba(0,0,0,.7) 80%, hsla(0,0%,100%,0) 0, hsla(0,0%,100%,0) 90%, rgba(0,0,0,.7) 0, rgba(0,0,0,.7)), linear-gradient(116.82deg, ${$theme.colors.gradientGoldStart}, ${$theme.colors.gradientGoldEnd})`,
}))

export const BannerArea = styled('div', {
  gridArea: 'banner',
  width: '100%',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
})

export const SidebarArea = styled('aside', {
  gridArea: 'sidebar',
  zIndex: 1000,
})

export const MainContent = styled('main', {
  gridArea: 'content',
  padding: '1rem',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
})

// ─── Card / Layout ───────────────────────────────────────────

export const Card = styled('div', ({ $theme }) => ({
  padding: '1rem',
  marginBottom: '2rem',
  backgroundColor: $theme.colors.white,
  borderRadius: '1rem',
  boxShadow: $theme.lighting.shadowPrimary,
  maxWidth: '100%',
  boxSizing: 'border-box',
}))

export const BlueCard = styled('div', ({ $theme }) => ({
  padding: '1rem',
  marginBottom: '2rem',
  backgroundColor: $theme.colors.backgroundSecondary,
  borderRadius: '1rem',
  boxShadow: $theme.lighting.shadowPrimary,
  maxWidth: '100%',
  boxSizing: 'border-box',
  marginLeft: '1rem',
  marginRight: '1rem',
}))

export const HalfCard = styled(Card, {
  width: 'calc(50% - 1rem)',
  marginLeft: '0.5rem',
  marginRight: '0.5rem',
})

// ─── Typography ──────────────────────────────────────────────

export const Header1 = styled('h1', ({ $theme }) => ({
  margin: '1rem 10px 10px 10px',
  color: $theme.colors.primary,
  ...$theme.typography.HeadingMedium,
}))

export const Header2 = styled('h2', ({ $theme }) => ({
  margin: '0.5rem 5px 5px 5px',
  color: $theme.colors.primary,
}))

// ─── Form utilities ─────────────────────────────────────────

export const FloatingLabel = styled('div', ({ $theme }) => ({
  position: 'absolute',
  top: '0.375rem',
  left: '0.5rem',
  height: '1rem',
  fontSize: '0.75rem',
  color: $theme.colors.grey,
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
  backgroundColor: 'transparent',
}))

export const EndEnhancerIconContainer = styled('div', {
  ...flexStyles.centerY,
  marginRight: '.5rem',
})

export const EnhancerRow = styled('div', {
  ...flexStyles.centerY,
  gap: '4px',
})

export const SelectWrapper = styled('div', {
  position: 'relative',
})

export const SelectCopyOverlay = styled('div', {
  position: 'absolute',
  right: '2.5rem',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
})

export const SDOutlineButton = (props) =>
  React.createElement(SDButtonNew, {
    block: true,
    overrides: {
      BaseButton: {
        style: ({ $theme }) => ({
          borderTopLeftRadius: '.375rem',
          borderTopRightRadius: '.375rem',
          borderBottomLeftRadius: '.375rem',
          borderBottomRightRadius: '.375rem',
          borderTopWidth: '1px',
          borderBottomWidth: '1px',
          borderLeftWidth: '1px',
          borderRightWidth: '1px',
          borderStyle: 'solid',
          borderColor: $theme.colors.primaryAlt,
          backgroundColor: 'transparent',
          fontSize: '0.875rem',
          color: $theme.colors.primary400,
          ':hover': {
            backgroundColor: $theme.colors.primary400,
            color: $theme.colors.white,
          },
        }),
      },
    },
    ...props,
  })
